import { type ChangeEvent, useRef, useState } from 'react'

import { PrimaryButton, RadioGroup, Space } from '@procurify/ui'
import { nanoid } from 'nanoid'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { type ConversationPaymentMethodOption } from '../../value-objects/ConversationTurnItem'

export interface IPaymentMethodSelectionProps {
  options: ConversationPaymentMethodOption[]
  defaultOptionId: Nullable<string>
  submitText: Nullable<string>
  disabled?: boolean
  onSubmit: (option: ConversationPaymentMethodOption) => void
}

export const PaymentMethodSelection = ({
  options,
  defaultOptionId,
  submitText,
  disabled = false,
  onSubmit,
}: IPaymentMethodSelectionProps) => {
  const intl = useProcurifyIntl()
  const nameRef = useRef(`payment-method-${nanoid()}`)
  const [selectedId, setSelectedId] = useState(defaultOptionId)

  const selectedOption = options.find((option) => option.id === selectedId)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedId(event.target.value)
  }

  const handleSubmit = () => {
    if (selectedOption) onSubmit(selectedOption)
  }

  if (options.length === 0) return null

  return (
    <Space direction='vertical' size='medium'>
      <RadioGroup
        name={nameRef.current}
        value={selectedId ?? ''}
        onChange={handleChange}
      >
        {options.map((option) => (
          <RadioGroup.CardItem
            key={option.id}
            value={option.id}
            label={option.label}
            description={option.description}
            infoBadgeText={option.badge ?? undefined}
            isDisabled={disabled || option.isDisabled}
          />
        ))}
      </RadioGroup>
      <PrimaryButton
        analyticsId='conversation-payment-method-submit-button'
        disabled={disabled || !selectedOption}
        onClick={handleSubmit}
      >
        {submitText ?? intl.formatMessage(messages.submitButton)}
      </PrimaryButton>
    </Space>
  )
}
